'use client';

import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { getConsentSnapshot, getServerConsentSnapshot, subscribeConsent } from '@/contexts/consent/store';
import { mixpanelClient } from '@/contexts/mixpanel/client';
import { captureEventProperties } from '@/contexts/mixpanel/event-properties';
export type { MixpanelTrackOptions, TrackProps } from '@/contexts/mixpanel/client';
export { MixpanelPageTracker } from '@/contexts/mixpanel/page-tracker';
export { useMixpanelEventProperties, type EventPropertiesSource } from '@/contexts/mixpanel/event-properties';

type Client = typeof mixpanelClient;
export type MixpanelContextValue = Pick<Client, 'logger' | 'getDistinctId' | 'identify' | 'setPeopleOnce' | 'register' | 'resetIdentity' | 'confirmAnonymous'> & {
    /** 最终分析权限，不是 SDK ready 状态。 */
    enabled: boolean;
};

const noop = () => {};
const MixpanelContext = createContext<MixpanelContextValue>({
    enabled: false, logger: () => false, getDistinctId: () => null, identify: noop, setPeopleOnce: noop,
    register: noop, resetIdentity: noop, confirmAnonymous: noop,
});

export function MixpanelProvider({ children, eventMirror }: {
    /** 应用子树，不等待 SDK。 */
    children: ReactNode;
    /** App 注入既有双写，不让 Mixpanel 客户端依赖或初始化其它 SDK。 */
    eventMirror?: Client['logger'];
}) {
    const consent = useSyncExternalStore(subscribeConsent, getConsentSnapshot, getServerConsentSnapshot);
    useLayoutEffect(() => {
        const unsubscribe = subscribeConsent(mixpanelClient.syncConsent);
        void mixpanelClient.start();
        return () => { unsubscribe(); mixpanelClient.dispose(); };
    }, []);
    const logger = useCallback<Client['logger']>((name, props, options) => {
        if (!getConsentSnapshot().analyticsAllowed || (options?.kind === 'terminal' && !options.insertId)) return false;
        let onceKeys: string[] = [];
        if (options?.onceKey) {
            try {
                const stored: unknown = JSON.parse(window.sessionStorage.getItem('mixpanel_once_keys') ?? '[]');
                if (Array.isArray(stored)) onceKeys = stored.filter((key): key is string => typeof key === 'string');
            } catch { /* 存储不可用不阻断事件，沿用 Web 的容错语义。 */ }
            if (onceKeys.includes(options.onceKey)) return false;
        }
        // 两个渠道共用事件发生时的同一份业务快照，异步发送不再回读可变状态。
        const snapshot = captureEventProperties(name, props);
        let accepted = false;
        try { accepted = mixpanelClient.logger(name, snapshot, options); }
        catch { console.error('[analytics] Mixpanel event failed'); }
        try { accepted = (eventMirror?.(name, snapshot, options) ?? false) || accepted; }
        catch { console.error('[analytics] event mirror failed'); }
        if (accepted && options?.onceKey) {
            try { window.sessionStorage.setItem('mixpanel_once_keys', JSON.stringify([...onceKeys, options.onceKey])); }
            catch { /* 存储不可用不阻断其它渠道。 */ }
        }
        return accepted;
    }, [eventMirror]);
    const value = useMemo<MixpanelContextValue>(() => ({
        enabled: consent.analyticsAllowed,
        logger,
        getDistinctId: mixpanelClient.getDistinctId,
        identify: mixpanelClient.identify,
        setPeopleOnce: mixpanelClient.setPeopleOnce,
        register: mixpanelClient.register,
        resetIdentity: mixpanelClient.resetIdentity,
        confirmAnonymous: mixpanelClient.confirmAnonymous,
    }), [consent.analyticsAllowed, logger]);
    return <MixpanelContext.Provider value={value}>{children}</MixpanelContext.Provider>;
}

export function useMixpanel(): MixpanelContextValue {
    return useContext(MixpanelContext);
}
